<script setup lang="ts">
  import type { AddressMeterPointDetails } from "@/api/model"
  import { useGetOfgemUsageProfiles } from "@/api/usage-profiles/usage-profiles"
  import { fieldRequiredMessage } from "@/constants/errorMessages"
  import { ExternalRoute } from "@/constants/externalRoute"
  import { MyAccountRoute } from "@/constants/routes/myAccount"
  import { MPAN_REGEX, MPRN_REGEX } from "@/lib/regexHelper"
  import { useCotMoveIn } from "@/lib/useCotMoveIn"
  import { useStepWizard } from "@/lib/useStepWizard"
  import { AddressFinderModel, AddressNotListed } from "@/types/AddressType"
  import { toTypedSchema } from "@vee-validate/zod"
  import { templateRef } from "@vueuse/core"
  import { useForm } from "vee-validate"
  import { computed, nextTick, onMounted, ref, watch } from "vue"
  import { z } from "zod"
  import TrustpilotWidget from "@/components/shared/TrustpilotWidget.vue"
  import { AddressFinder } from "@/components/ui/address-finder"
  import { Alert, AlertDescription } from "@/components/ui/alert"
  import Button from "@/components/ui/button/Button.vue"
  import { Divider } from "@/components/ui/divider"
  import { TextInput } from "@/components/ui/inputs/text-input"
  import { Link } from "@/components/ui/link"
  import { StepWizardProgressCard } from "@/components/ui/step-wizard"

  import {
    MPAN_EMPTY_MESSAGE,
    MPAN_ERROR_MESSAGE,
    MPRN_ERROR_MESSAGE,
  } from "@/views/enrolment/enrolmentHelpers"
  import { useEnrolment } from "@/views/enrolment/useEnrolment"

  import USwitchLogo from "@/assets/images/uswitch-logo.png"

  const { goNext } = useStepWizard()
  const {
    address,
    updateAddress,
    updateExtras,
    updateMprnOnlyAddresses,
    updateOfgemUsageProfiles,
    getCrossFlowAddress,
    clearCrossFlowAddress,
  } = useEnrolment()

  const {
    refetch: fetchProfiles,
    isFetching: isLoadingProfiles,
    isError: isProfilesError,
  } = useGetOfgemUsageProfiles({ query: { enabled: false } })

  const {
    setCrossFlowAddress,
    clearCrossFlowAddress: clearMoveInCrossFlowAddress,
  } = useCotMoveIn()

  const defaultPostcode = ref<string>()
  const defaultAddress = ref<AddressMeterPointDetails>()
  let autoSubmitFullAddress: string | null = null

  watch(
    () => address.value.postcode,
    async (postcode) => {
      if (postcode && !defaultPostcode.value) {
        await nextTick()
        if (!defaultPostcode.value) defaultPostcode.value = postcode
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    const crossFlowAddress = getCrossFlowAddress()
    if (crossFlowAddress) {
      clearCrossFlowAddress()
      autoSubmitFullAddress = crossFlowAddress.fullAddress ?? null
      defaultAddress.value = crossFlowAddress
      defaultPostcode.value = crossFlowAddress.address.postcode
    }
  })

  const modelValue = ref<AddressFinderModel>()

  const isAlreadyOnSupply = computed(
    () =>
      !!modelValue.value &&
      modelValue.value !== AddressNotListed &&
      !!modelValue.value?.billingAccountId,
  )

  const addressValue = computed((): AddressMeterPointDetails | undefined => {
    if (!!modelValue.value && modelValue.value !== AddressNotListed) {
      return modelValue.value
    }
    return undefined
  })

  watch(addressValue, (address) => {
    if (address?.billingAccountId) {
      setCrossFlowAddress(address)
    } else {
      clearMoveInCrossFlowAddress()
    }
  })

  const isAddressNotListed = computed(
    () => modelValue.value === AddressNotListed,
  )

  const { handleSubmit: handleManualSubmit, setFieldValue } = useForm({
    validationSchema: toTypedSchema(
      z.object({
        addressLine1: z
          .string({ required_error: fieldRequiredMessage })
          .min(1, fieldRequiredMessage)
          .min(3, "Please enter a valid address."),
        addressLine2: z
          .string({ required_error: fieldRequiredMessage })
          .min(1, fieldRequiredMessage)
          .min(3, "Please enter a valid address."),
        mpan: z
          .string({ required_error: MPAN_EMPTY_MESSAGE })
          .min(1, MPAN_EMPTY_MESSAGE)
          .regex(MPAN_REGEX, MPAN_ERROR_MESSAGE),
        mprn: z
          .string()
          .refine((val) => !val || MPRN_REGEX.test(val), MPRN_ERROR_MESSAGE)
          .optional(),
      }),
    ),
  })

  watch(
    () => address.value.mprn,
    (mprn) => setFieldValue("mprn", mprn),
  )

  const addressFinder = templateRef("addressFinder")

  const saveAddress = (
    selected: AddressMeterPointDetails | undefined,
    manual?: {
      addressLine1: string
      addressLine2: string
      mpan: string
      mprn?: string
    },
  ) => {
    const addressLine1 = selected?.address.addressLine1 ?? manual?.addressLine1
    const addressLine2 = selected?.address.addressLine2 ?? manual?.addressLine2
    const addressLine3 = selected?.address.addressLine3 ?? ""
    const postcode =
      selected?.address.postcode ?? addressFinder.value?.postcode ?? ""
    const countryCode = selected?.address.countryCode ?? "GB"
    const mpan = selected?.mpan ?? manual?.mpan
    const mprn = selected?.mprn ?? manual?.mprn
    const fullAddress =
      selected?.fullAddress ??
      [addressLine1, addressLine2, postcode].filter(Boolean).join(", ")

    updateAddress({
      addressLine1,
      addressLine2,
      addressLine3,
      postcode,
      countryCode,
      mpan,
      mprn,
      isMpanSmartMeter: selected?.isMpanSmartMeter,
    })
    updateExtras({
      fullAddress,
      isManualEntry: !selected,
      missingMPRN: !mprn,
    })
    updateMprnOnlyAddresses(addressFinder.value?.mprnOnlyAddresses ?? [])
  }

  const fetchProfilesAndProceed = async () => {
    const { data, isError } = await fetchProfiles()
    if (isError || !data) return
    updateOfgemUsageProfiles(data)
    goNext()
  }

  const onSubmit = async () => {
    const postcodeValidation = await addressFinder.value?.onSubmitPostcode()
    if (!postcodeValidation?.valid) return

    if (isAddressNotListed.value) {
      await handleManualSubmit(async (values) => {
        saveAddress(undefined, values)
        await fetchProfilesAndProceed()
      })()
      return
    }

    if (
      modelValue.value &&
      modelValue.value !== AddressNotListed &&
      !isAlreadyOnSupply.value
    ) {
      saveAddress(modelValue.value as AddressMeterPointDetails)
      await fetchProfilesAndProceed()
    }
  }

  watch(addressValue, (selected) => {
    if (!autoSubmitFullAddress) return
    const carried = autoSubmitFullAddress
    autoSubmitFullAddress = null
    if (selected?.fullAddress === carried) onSubmit()
  })
</script>

<template>
  <form
    class="flex w-full flex-wrap justify-center"
    novalidate
    @submit.prevent="onSubmit"
  >
    <StepWizardProgressCard title="Your address">
      <p class="mb-4 sm:mb-6">
        Enter your postcode to get your personalised quote. Already a So Energy
        customer?
        <Link
          class="link"
          :to="ExternalRoute.renewal"
          data-testid="existing-customer-renewal-link"
          >Renew your tariff instead</Link
        >.
      </p>
      <AddressFinder
        ref="addressFinder"
        v-model="modelValue"
        :postcode="address.postcode"
        :default-postcode="defaultPostcode"
        :default-address="defaultAddress"
        filter-mpan
      />
      <div v-if="isAddressNotListed" class="mt-8 sm:mt-16">
        <h4 class="h4 mb-2">If you can't find your address</h4>
        <p class="mb-4 text-base text-secondary-500">
          Please enter your address details below. You can find your 13-digit
          MPAN or MPRN on your current bill.
        </p>
        <div class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <TextInput name="addressLine1" label="Address line 1" />
          <TextInput name="addressLine2" label="Address line 2" />
          <TextInput
            name="mpan"
            label="MPAN (Electricity)"
            type="tel"
            maxlength="13"
            inputmode="numeric"
          />
          <TextInput
            name="mprn"
            label="MPRN (Gas)"
            type="tel"
            extra-label="Optional"
            extra-label-variant="regular"
            maxlength="10"
            inputmode="numeric"
          />
        </div>
      </div>
      <Alert
        v-if="isAlreadyOnSupply"
        variant="warning"
        class="mt-4 sm:mt-8"
        data-testid="on-supply-alert"
      >
        <AlertDescription>
          This address is already supplied by So Energy. If you're moving in,
          you can
          <Link class="link" :to="MyAccountRoute.cotMoveInFindAddress"
            >complete a move-in</Link
          >
          instead.
        </AlertDescription>
      </Alert>
      <Alert
        v-if="isProfilesError"
        variant="error"
        class="mt-4 sm:mt-8"
        data-testid="profiles-error-alert"
      >
        <AlertDescription>
          Something went wrong. Please try again. If the error persists please
          <Link class="link" :to="ExternalRoute.getSupport">contact us</Link>.
        </AlertDescription>
      </Alert>
      <template #next_button>
        <Button
          variant="tonal"
          type="submit"
          size="md"
          :disabled="isAlreadyOnSupply"
          :loading="isLoadingProfiles"
        >
          Next
        </Button>
      </template>
    </StepWizardProgressCard>
  </form>
  <div
    class="flex flex-col items-center justify-center gap-6 px-6 pb-6 pt-10 sm:flex-row sm:gap-8 sm:pb-12 sm:pt-12"
  >
    <TrustpilotWidget
      class="!w-[225px] sm:!w-[167px] [&_iframe]:!h-[100px] sm:[&_iframe]:!h-[80px]"
    />
    <Divider orientation="horizontal" class="!w-[56px] sm:hidden" />
    <Divider orientation="vertical" class="hidden !h-[56px] sm:block" />
    <img :src="USwitchLogo" alt="" class="w-[241px]" />
  </div>
</template>
